<script setup lang="ts">
import { type FormKitOptionsItem } from "@formkit/inputs";
import {
  KitColour,
  type KitResponse,
  KitType,
  type UpdateKitDetailsRequest,
} from "mws-catalog-ts-sdk";
import type { AdminPassepartoutImageResponse } from "mws-framethegame-ts-sdk";
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";

import CardVue from "@/common/components/card/CardVue.vue";
import DescriptionList from "@/common/components/description/DescriptionList.vue";
import DescriptionListRow from "@/common/components/description/DescriptionListRow.vue";
import DocumentUploadDownload from "@/common/components/DocumentUploadDownload.vue";
import FormSection from "@/common/components/FormSection/FormSection.vue";
import BaseIcon from "@/common/components/icons/BaseIcon.vue";
import { formKitDate } from "@/common/utils/dateUtils";
import { startCase } from "@/common/utils/startCase";
import {
  adminPassePartoutImageApi,
  useGetPassePartoutsByTeamIdQuery,
} from "@/framing/api/queries";
import TeamPassePartoutAutocomplete from "@/framing/components/TeamPassePartoutAutocomplete.vue";
import {
  useUpdateCatalogKitAssetsMutation,
  useUpdateCatalogKitMutation,
} from "@/kits/api/queries";

const selectedTeam = ref<{ id: string; name: string } | null>(null);

const initialPassePartoutLoader = async (
  value: string | undefined,
  cachedOption: FormKitOptionsItem<unknown> | undefined,
) => {
  if (cachedOption) return cachedOption;

  if (!value) return;

  const response =
    await adminPassePartoutImageApi.adminPassepartoutImagesPassepartoutIdGet({
      passepartoutId: value,
    });

  return formatPassePartoutOption(response.data);
};

const {
  data: teamPassePartouts,
  isLoading: teamPassePartoutsLoading,
  isFetching: teamPassePartoutsFetching,
} = useGetPassePartoutsByTeamIdQuery(() => kit?.teamId);

const formatPassePartoutOption = (
  passePartout: AdminPassepartoutImageResponse,
) => ({
  label: `${selectedTeam.value?.name || "Team"} Passe Partout`,
  subTitle: `Updated: ${new Date(passePartout.dateModified).toLocaleDateString()}`,
  value: passePartout.id,
  imageUrl: passePartout.previewImageUrl,
});

definePage({
  alias: ["/teams/:teamId?/kits/:kitId/details"],
  meta: {
    auth: ["Kits.Read"],
  },
});

const { kit } = defineProps<{
  kit: KitResponse;
}>();

const toast = useToast();
const { mutateAsync: updateKit } = useUpdateCatalogKitMutation();
const { mutateAsync: updateKitAssets } = useUpdateCatalogKitAssetsMutation();

if (kit && kit.teamId) {
  selectedTeam.value = {
    id: kit.teamId,
    name: "Team",
  };
}

const initialFormData = computed(() => {
  if (!kit) return {};

  return {
    ...kit,
    dueDate: kit.dueDate ? formKitDate(kit.dueDate) : undefined,
    customPassepartoutImageId: kit.customPassepartoutImageId,
  };
});

const onSubmit = async (form: UpdateKitDetailsRequest) => {
  const request = {
    kitId: kit.id,
    request: {
      ...form,
      customPassepartoutImageId: form.customPassepartoutImageId,
    },
  };
  await updateKit(request);
  toast.success("Kit updated successfully");
};

const onUploadDefaultImage = async (imageUrl: string) => {
  const request = {
    kitId: kit.id,
    request: {
      ...kit,
      type: kit.type ?? KitType.Home,
      colour: kit.colour ?? KitColour.White,
      defaultImage: imageUrl,
    },
  };
  await updateKit(request);
  toast.success("Image updated successfully");
};

const onUploadWorkfiles = async (workFilesUrl: string) => {
  const request = {
    kitId: kit.id,
    request: {
      assetsZipUrl: null,
      workFilesZipUrl: workFilesUrl,
    },
  };
  await updateKitAssets(request);
  toast.success("Successfully updated workfiles!");
};

const onUploadAssets = async (assetsUrl: string) => {
  const request = {
    kitId: kit.id,
    request: {
      assetsZipUrl: assetsUrl,
      workFilesZipUrl: null,
    },
  };
  await updateKitAssets(request);
  toast.success("Successfully updated assets!");
};

const workfilesUploadRef = ref();
const assetsUploadRef = ref();

const onReuploadWorkfiles = () => {
  workfilesUploadRef.value?.openUploader();
};

const onReuploadAssets = () => {
  assetsUploadRef.value?.openUploader();
};
</script>

<template>
  <div>
    <CardVue>
      <FormKitForm
        v-if="kit"
        :value="initialFormData"
        submit-label="Submit"
        :submit-attrs="{ 'data-test': 'kit-details-submit' }"
        @submit="onSubmit"
      >
        <FormSection
          :uses-column-layout="true"
          title="General Information"
          :display-divider="false"
          description="Automatically attached and created fields"
        >
          <FormKit
            type="hidden"
            name="teamId"
          />
          <FormKit
            type="text"
            name="name"
            validation="required|trim"
            label="Kit name"
          />
        </FormSection>
        <FormSection
          :uses-column-layout="true"
          title="System Settings"
          description="These settings will be used during automatic product creation.
    Default image, player position and FTG mockup sets are copied over to product."
        >
          <div class="flex justify-between gap-4">
            <div class="flex flex-col flex-1">
              <DescriptionList class="mb-4">
                <DescriptionListRow title="Player Position">
                  {{ startCase(kit?.kitPlayerPosition ?? "Not set") }}
                </DescriptionListRow>
                <DescriptionListRow title="Kit Type">
                  {{ startCase(kit?.type ?? "Not set") }}
                </DescriptionListRow>
                <DescriptionListRow title="Special Edition">
                  {{ kit?.isSpecialEdition ? "Yes" : "No" }}
                </DescriptionListRow>
                <DescriptionListRow title="Kit Colour">
                  {{ startCase(kit?.colour ?? "Not set") }}
                </DescriptionListRow>
              </DescriptionList>
              <TeamPassePartoutAutocomplete
                :selected-team="selectedTeam"
                :team-passe-partouts="teamPassePartouts"
                :team-passe-partouts-loading="teamPassePartoutsLoading"
                :team-passe-partouts-fetching="teamPassePartoutsFetching"
                :initial-passe-partout-loader="initialPassePartoutLoader"
                name="customPassepartoutImageId"
              />
            </div>
            <FormKit
              type="uploader"
              name="defaultImage"
              help="Default Image"
              hide-name="true"
              preview-full="true"
              accept="image/*"
              class="!min-h-60"
              @input="(value) => onUploadDefaultImage(value as string)"
            />
          </div>
          <FormKit
            type="text"
            name="description"
            label="Description"
          />
        </FormSection>

        <FormSection
          v-if="kit && kit.asanaUrl"
          :uses-column-layout="true"
          title="Design Related Information"
          description="NOTE: A design task has already been created for this kit. If changes are required please contact design department directly."
          :display-divider="false"
        >
          <DescriptionList>
            <DescriptionListRow title="Asana URL">
              <div class="flex items-center">
                <a
                  class="text-indigo-500 hover:text-indigo-700 mr-2"
                  :href="kit.asanaUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ kit.asanaUrl }}
                </a>
              </div>
            </DescriptionListRow>
            <DescriptionListRow title="Workfiles zip">
              <div class="flex items-center gap-4">
                <DocumentUploadDownload
                  ref="workfilesUploadRef"
                  :document-url="kit.workFilesZipUrl"
                  document-type="Workfiles"
                  upload-permission="Kits.Write"
                  download-permission="Kits.Read"
                  accept="application/zip,.zip"
                  @upload="onUploadWorkfiles"
                />
                <div
                  v-if="kit.workFilesZipUrl"
                  class="cursor-pointer flex items-center"
                  @click="onReuploadWorkfiles"
                >
                  <span>Reupload</span>
                  <BaseIcon
                    name="heroicons:arrow-path"
                    class="ml-2"
                  />
                </div>
              </div>
            </DescriptionListRow>
            <DescriptionListRow title="Assets zip">
              <div class="flex items-center gap-2">
                <DocumentUploadDownload
                  ref="assetsUploadRef"
                  :document-url="kit.assetsZipUrl"
                  document-type="Assets"
                  upload-permission="Kits.Write"
                  download-permission="Kits.Read"
                  accept="application/zip,.zip"
                  @upload="onUploadAssets"
                />
                <div
                  v-if="kit.assetsZipUrl"
                  class="cursor-pointer flex items-center"
                  @click="onReuploadAssets"
                >
                  <span>Reupload</span>
                  <BaseIcon
                    name="heroicons:arrow-path"
                    class="ml-2"
                  />
                </div>
              </div>
            </DescriptionListRow>
            <DescriptionListRow title="Font Colour">
              {{ kit?.fontColour || "Not set" }}
            </DescriptionListRow>
            <DescriptionListRow title="Font Type">
              {{ startCase(kit?.fontType ?? "Not set") }}
            </DescriptionListRow>
            <DescriptionListRow title="First Match Date">
              {{
                kit && kit.dueDate
                  ? new Date(kit.dueDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Not set"
              }}
            </DescriptionListRow>
            <DescriptionListRow title="Notes For Design">
              <div class="whitespace-pre-wrap">{{ kit?.note }}</div>
            </DescriptionListRow>
          </DescriptionList>
        </FormSection>
      </FormKitForm>
    </CardVue>
  </div>
</template>
